import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight } from 'lucide-react';

import ParticleBackground from './ParticleBackground';

const Home = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => setLoaded(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, type: 'spring', stiffness: 100 } }
  };

  const TechGrid = () => (
    <div className="absolute inset-0 z-0">
      <div className="absolute w-full h-full grid grid-cols-12 gap-px opacity-5">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-r border-gray-300 h-full"></div>
        ))}
      </div>
      <div className="absolute w-full h-full grid grid-rows-12 gap-px opacity-5">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-b border-gray-300 w-full"></div>
        ))}
      </div>
    </div>
  );

  const ScanLine = () => (
    <motion.div
      className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-400/10 to-transparent h-32"
      animate={{ top: ['-10%', '110%'] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
    />
  );

  return (
    <>
      <AnimatePresence>
        {!loaded && (
          <motion.div
            className="fixed inset-0 bg-black z-50 flex flex-col items-center justify-center"
            exit={{ opacity: 0, y: -20, transition: { duration: 0.8, ease: 'easeInOut' } }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-6"
            >
              <img src="/images/icon.png" alt="Logo" className="h-16 w-auto" />
            </motion.div>
            <motion.div
              className="w-48 h-2 bg-gray-700 rounded-full overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <motion.div
                className="h-full bg-gradient-to-r from-gray-400 to-white"
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ duration: 0.7, delay: 0.4, ease: 'easeInOut' }}
              />
            </motion.div>
            <motion.div
              className="mt-4 text-white"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              Initializing secure connection...
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
        <ParticleBackground />
        <TechGrid />
        <ScanLine />

        <div className="container mx-auto px-4 z-20 relative py-24">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Logo */}
            <motion.div variants={itemVariants} className="mb-12">
              <img
                src="/images/reticuli-logo.png"
                alt="Reticuli Technologies"
                className="h-96 md:h-[28rem] mx-auto"
              />
            </motion.div>

            {/* Stage indicator */}
            <motion.div variants={itemVariants} className="mb-8 flex justify-center">
              <span className="inline-flex items-center px-3 py-1 rounded-full border border-gray-700/50 bg-gray-900/50 text-xs tracking-wide text-gray-500 uppercase">
                Early-stage R&D company
              </span>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/about"
                className="group px-8 py-4 bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-600 hover:to-gray-800 text-white font-medium rounded-md transition-all duration-300 flex items-center justify-center shadow-lg shadow-black/30 border border-gray-600/30"
              >
                Learn More
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={18} />
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 border border-gray-700 hover:border-gray-500 text-gray-300 hover:text-white font-medium rounded-md transition-all duration-300 flex items-center justify-center"
              >
                Contact Us
                <ChevronRight className="ml-2" size={18} />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-gray-600 flex items-start justify-center p-2">
            <motion.div 
              className="w-1.5 h-1.5 bg-gray-400 rounded-full"
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* Mission Statement Section */}
      <section className="relative py-24 bg-gradient-to-b from-black via-gray-950 to-gray-950">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Section Header */}
            <div className="text-center mb-12">
              <motion.p
                className="text-sm uppercase tracking-widest text-gray-500 mb-4"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                Our Mission
              </motion.p>
              <div className="flex items-center justify-center gap-3">
                <div className="w-16 h-px bg-gradient-to-r from-transparent to-gray-700" />
                <div className="w-2 h-2 rotate-45 bg-gray-700" />
                <div className="w-16 h-px bg-gradient-to-l from-transparent to-gray-700" />
              </div>
            </div>

            {/* Content */}
            <div className="relative">
              <div className="absolute -top-6 -left-2 text-7xl text-gray-800/40 font-serif">"</div>

              <div className="relative z-10 space-y-6 text-lg text-gray-300 font-light leading-relaxed pl-6 border-l-2 border-gray-800">
                <p>
                  Reticuli Technologies is an early-stage R&D company researching <span className="text-white font-medium">neuromorphic computing</span> for cybersecurity applications and <span className="text-white font-medium">post-quantum cryptography</span>.
                </p>

                <p>
                  We're exploring architectures that mirror biological efficiency and cryptographic schemes built to withstand future quantum-computing threats.
                </p>

                <p className="text-gray-400">
                  The future is engineered, not inherited.
                </p>
              </div>

              {/* Company Attribution */}
              <div className="mt-10 pl-6">
                <p className="text-white font-medium">— Reticuli Technologies</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="relative py-20 bg-gray-950 border-t border-gray-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-6">
            {[
              {
                title: "About",
                description: "Learn more about Reticuli Technologies and our research focus areas",
                link: "/about",
                linkText: "Learn More"
              },
              {
                title: "Research",
                description: "Where our published work and open-source projects will be listed",
                link: "/research",
                linkText: "View"
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group bg-gray-900/40 border border-gray-800/50 rounded-lg p-6 hover:border-gray-700/50 hover:bg-gray-900/60 transition-all duration-300"
              >
                <h3 className="text-xl font-medium text-white mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm mb-4 leading-relaxed">{item.description}</p>
                <Link
                  to={item.link}
                  className="inline-flex items-center text-gray-400 hover:text-white text-sm transition-colors group-hover:text-gray-300"
                >
                  {item.linkText}
                  <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Subtle CTA Section */}
      <section className="relative py-16 bg-black border-t border-gray-800/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-2xl mx-auto text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-500 text-lg mb-6">
              Interested in what we're building?
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center text-gray-300 hover:text-white transition-colors border-b border-gray-700 hover:border-gray-500 pb-1"
            >
              Get in touch
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Home;